import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/context/LanguageContext';

export const Step4Summary = ({ credits, onGenerate, generating, orientation, summary }) => {
  const { t } = useI18n();
  const enough = credits >= 1;
  const size = orientation === 'portrait' ? '1080 x 1920' : '1920 x 1080';

  return (
    <div className="space-y-7">
      <div>
        <h2 className="font-display text-lg font-semibold">{t('step4.title')}</h2>
        <p className="mt-1.5 text-sm text-[hsl(var(--text-muted))]">{t('step4.desc', { size })}</p>
      </div>

      <div className="rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] p-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--brand-red))]" />
          <h3 className="text-sm font-semibold">{t('step4.summaryTitle')}</h3>
        </div>
        <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
          {summary.map((s) => (
            <div key={s.label} className="flex justify-between gap-2 border-b border-[hsl(var(--border-soft))] pb-1.5">
              <dt className="text-[hsl(var(--text-muted))]">{s.label}</dt>
              <dd className="truncate text-right font-medium text-[hsl(var(--text))]">{s.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {!enough && (
        <div
          data-testid="insufficient-credits-warning"
          className="flex items-start gap-2.5 rounded-[var(--radius-md)] border border-[hsl(var(--brand-red))] bg-[hsl(var(--surface-2))] p-3.5"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand-red))]" />
          <div className="text-sm">
            <p className="font-semibold">{t('step4.noCreditsTitle')}</p>
            <p className="mt-0.5 text-[hsl(var(--text-muted))]">
              <Link to="/hesabim" className="font-semibold text-[hsl(var(--brand-red))] hover:underline">
                {t('step4.noCreditsLink')}
              </Link>{' '}
              {t('step4.noCreditsDesc')}
            </p>
          </div>
        </div>
      )}

      <Button
        onClick={onGenerate}
        disabled={generating || !enough}
        data-testid="generate-button"
        className="h-12 w-full bg-[hsl(var(--brand-red))] text-base font-semibold text-white hover:bg-[hsl(var(--brand-red-2))] active:scale-[0.99] disabled:opacity-60"
      >
        {generating ? (
          <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t('step4.generating')}</>
        ) : (
          <><Wand2 className="mr-2 h-5 w-5" /> {t('step4.generate')}</>
        )}
      </Button>
    </div>
  );
};
